import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ICS204_SURFACE } from 'components/IAP/planningView/widgets/AssignmentList/assignmentCardStyles';
import {
  Ics213RrListRow,
  type Ics213RrListRowData,
} from 'components/IAP/ResourceRequests/ics213rr/Ics213RrListRow';

type Ics213RrListTableProps = {
  data: Ics213RrListRowData[];
  expandedId: string | null;
  onToggleRow: (requestId: string) => void;
  scrollToId?: string | null;
  memberPositions: { positionId: string; positionName: string }[];
  reqCount: Record<string, number>;
  readOnly?: boolean;
};

const columnHelper = createColumnHelper<Ics213RrListRowData>();

const columns = [
  columnHelper.display({
    id: 'expand',
    header: () => <span className="tw:sr-only">Expand</span>,
  }),
  columnHelper.accessor('request_number', {
    id: 'requestNumber',
    header: 'Request number',
  }),
  columnHelper.accessor((row) => row.requestor_name ?? '', {
    id: 'submittedBy',
    header: 'Submitted by',
  }),
  columnHelper.accessor('statusLabel', {
    id: 'status',
    header: 'Status',
  }),
  columnHelper.accessor('resourcesSummary', {
    id: 'resources',
    header: 'Resources',
  }),
];

const SUMMARY_COLUMN_KEYS = [
  'requestNumber',
  'submittedBy',
  'status',
  'resources',
] as const;

/**
 * TanStack header table + tbody-per-row expand (mirrors AssignmentListTable).
 */
export function Ics213RrListTable({
  data,
  expandedId,
  onToggleRow,
  scrollToId,
  memberPositions,
  reqCount,
  readOnly = false,
}: Ics213RrListTableProps) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.resource_request_id,
  });

  return (
    <div
      data-testid="ics213rr-table-wrapper"
      className={cn(
        // overflow-hidden clips row fills to the rounded border so corners stay visible.
        'dark tw:w-full tw:min-w-0 tw:overflow-hidden tw:rounded-lg tw:border tw:border-[var(--border-primary)] tw:text-foreground',
        'tw:[&>[data-slot=table-container]]:min-w-0 tw:[&>[data-slot=table-container]]:!bg-transparent',
        ICS204_SURFACE
      )}
    >
      <Table className={cn(ICS204_SURFACE, 'tw:w-full tw:min-w-0')}>
        <TableHeader data-testid="ics213rr-column-header">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="tw:hover:bg-transparent tw:border-b tw:border-[var(--border-primary)]"
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={cn(ICS204_SURFACE, 'tw:text-[var(--text-weak)]')}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        {data.map((item) => (
          <Ics213RrListRow
            key={item.resource_request_id}
            item={item}
            summaryColumns={[...SUMMARY_COLUMN_KEYS]}
            columnCount={columns.length}
            expanded={expandedId === item.resource_request_id}
            onToggle={() => onToggleRow(item.resource_request_id)}
            memberPositions={memberPositions}
            reqCount={reqCount}
            readOnly={readOnly}
            scrollOnExpand={scrollToId === item.resource_request_id}
          />
        ))}
        {data.length === 0 ? (
          <tbody>
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="tw:h-24 tw:text-center tw:font-normal"
              >
                No resource requests.
              </TableCell>
            </TableRow>
          </tbody>
        ) : null}
      </Table>
    </div>
  );
}
